import React from 'react';
import { motion } from 'framer-motion';
import { Truck, Star, Clock, Award } from 'lucide-react';
import { getRegionMeta } from '../utils/region';
// "Why shop local" used to be its own /local-vs-national page. It's now the
// second half of this page, so the two stories (who we are / why local beats a
// national marketplace) read as one. The file still lives under pages/ to keep
// the diff small; the route itself now redirects to /about.
import { LocalVsNational } from '../pages/LocalVsNational';
const stats = [{
  label: 'Years Serving WA',
  value: '10+',
  icon: Clock
}, {
  label: 'Deliveries Made',
  value: '5,000+',
  icon: Truck
}, {
  label: 'Locally Owned',
  value: '100%',
  icon: Award
}, {
  label: 'Customer Rating',
  value: '5-Star',
  icon: Star
}];
export function About() {
  // Brand + area copy follow the active domain (253 / 206 / 425) so the
  // page reads as the regional identity the visitor arrived on.
  const region = getRegionMeta();
  const brand = `${region.code}Gravel`;
  return <div className="bg-[#f1ede6] dark:bg-[#292827] transition-colors duration-300 min-h-screen" data-id="element-104" data-mp="2ls0gahz3rl8y">
      {/* Hero Section */}
      <section className="relative pt-28 pb-24 lg:pt-40 lg:pb-28 bg-[#e8ede4] dark:bg-[#292827] overflow-hidden min-h-[440px] lg:min-h-[700px]" data-id="element-105" data-mp="2gqtagogisw4x">
        {/* Background Image */}
        <div className="absolute inset-0" data-id="element-106" data-mp="27sr8o6oypzgl">
          <img src="https://cdn.magicpatterns.com/uploads/ebxsCGdnf9doSY72o8VZYu/file_0000000013d071fd98aa813ad564b1e6.png" alt="Dump truck spreading gravel on a driveway" className="w-full h-full object-cover" data-id="element-107" data-mp="34hp65wo3kbhg" />
          <div className="absolute inset-0 bg-black/50" data-id="element-108" data-mp="2vp55kqo8vwtz" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-109" data-mp="2vn249ahn6h9y">
          <div className="text-center max-w-3xl mx-auto" data-id="element-110" data-mp="2qq1xexcyxahs">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block py-1 px-3 rounded-xl bg-[#FDAC53]/15 text-[#FDAC53] text-xs sm:text-sm font-bold tracking-wider uppercase mb-4 sm:mb-6" style={{
            color: "#ffffff"
          }} data-id="element-111" data-mp="3o5xyq7tjyjhh">
              Our Story
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 sm:mb-6 leading-tight" data-id="element-112" data-mp="b3lrfgvfeana">
              Building the foundation of our community,{' '}
              <span className="text-brand-orange" style={{
              color: "#03943D"
            }} data-id="element-113" data-mp="3ok35z1cel382">one load at a time.</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-base sm:text-lg md:text-xl text-stone-200 dark:text-stone-300 leading-relaxed" data-id="element-114" data-mp="3alms8qg4icgf">
              We are {brand} — your trusted local partner for premium
              landscaping materials and dependable delivery across the{' '}
              {region.name}.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Stats — one flat panel split into cells rather than four floating
            cards. The old version stacked a shadowed 2xl card, a 56px icon
            tile, a watermark numeral and heavy padding in each cell, which
            read as an enormous block on a phone. Now the icon sits inline
            beside the label and the whole strip is a single surface. */}
      <section className="relative z-20 -mt-10 lg:-mt-14 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-115" data-mp="3c3bmvfi22l9y">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} className="grid grid-cols-2 lg:grid-cols-4 rounded-xl overflow-hidden bg-white dark:bg-[#333333] border border-stone-200 dark:border-white/10 shadow-sm divide-x divide-y lg:divide-y-0 divide-stone-200 dark:divide-white/10" data-id="element-116" data-mp="3pyw508pymzgb">
          {stats.map(stat => <div key={stat.label} className="px-4 py-4 sm:px-5 sm:py-5 text-center" data-id="element-117" data-mp="1x33etiivgpii">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-stone-900 dark:text-white font-heading tracking-tight leading-none" data-id="element-118" data-mp="222y5bkyqjd31">
                {stat.value}
              </div>
              <div className="mt-2 flex items-center justify-center gap-1.5 text-stone-600 dark:text-stone-300" data-id="element-119" data-mp="19fbr7irvsq1x">
                <stat.icon className="w-3.5 h-3.5 text-[#03943D] shrink-0" aria-hidden="true" data-id="element-120" data-mp="1eeevv23y8u9d" />
                <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.12em] leading-tight" data-id="element-121" data-mp="1jboqcw673w42">
                  {stat.label}
                </span>
              </div>
            </div>)}
        </motion.div>
      </section>

      {/* Story Section */}
      <section id="our-story" className="py-14 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 scroll-mt-[140px]" data-id="element-122" data-mp="vmvut0thjq9z">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center" data-id="element-123" data-mp="o3wcyzloa6sq">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="lg:col-span-5" data-id="element-124" data-mp="j408kz92cdcn">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-stone-900 dark:text-white leading-tight mb-6" data-id="element-125" data-mp="95c28xqfxmos">
              More than just gravel. We deliver reliability.
            </h2>
            <div className="w-20 h-1.5 bg-[#FDAC53] rounded-full mb-8" style={{
            backgroundColor: "#04A05C"
          }} data-id="element-126" data-mp="d0lk3l4pdm0"></div>
            <p className="text-xl text-stone-600 dark:text-stone-300 font-medium leading-relaxed font-sub" data-id="element-127" data-mp="3rkuy7d0uo18s">
              Founded with a simple mission — to make ordering and receiving
              landscaping materials as easy as possible.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="lg:col-span-7 space-y-6 text-lg text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-128" data-mp="3dlmz0k4ost4l">
            <p data-id="element-129" data-mp="2tzk0q56dod2p">
              <strong className="text-stone-900 dark:text-white font-semibold" data-id="element-130" data-mp="2u03j1mzxx3h9">
                {brand}
              </strong>{' '}
              is a locally owned gravel delivery company serving Pierce and King
              counties — from Tacoma to Seattle and beyond. We specialize in
              bulk delivery of{' '}
              <strong className="text-stone-900 dark:text-white font-semibold" data-id="element-131" data-mp="3qo9fvnqmerkz">
                crushed rock, drain rock, pea gravel, road base, beauty bark,
                topsoil, and recycled aggregates
              </strong>{' '}
              — everything you need for driveways, landscaping, drainage, and
              construction projects.
            </p>
            <p data-id="element-132" data-mp="e8pe1kpj7s6s">
              We've built our reputation on fast delivery, fair pricing, and
              outstanding customer service. From a single 5-yard load of crushed
              gravel for your driveway to 20+ yards of topsoil for a commercial
              landscaping job, no order is too small or too large.
            </p>
            <p data-id="element-133" data-mp="xedexugiodmq">
              We deliver throughout{' '}
              <strong className="text-stone-900 dark:text-white font-semibold" data-id="element-134" data-mp="se7j0237s4yu">
                 Seattle, Bellevue,
               Kirkland, Redmond, Renton, Tacoma, Puyallup, Bonney Lake, Lakewood, Federal Way, Auburn,
                Kent, Sumner, Edgewood, Milton and surrounding Pierce & King County
                communities
              </strong>
              . Our experienced dump truck operators know the area and will
              place your material exactly where you need it.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why shop local — the former standalone page, kept whole (its own hero,
            comparison table, pull-quote, benefits carousel, FAQ, and closing CTA)
            so nothing was lost in the merge. The shared FAQ block that used to
            close this page was dropped: it duplicates the home page, and this
            section brings its own local-buying questions. */}
      <LocalVsNational data-id="element-135" data-mp="3ognjy6nlldqt" />
    </div>;
}